// A small matplotlib look-alike in SVG, so the engine's figures (figures.py, recording_figures.py) are drawn live in
// the browser from the data and redraw whenever the reviewer changes something. Units: px of the engine's PNG
// (figure inches x dpi), scaled to the page width.
import React from 'react';
import { amax, amin } from './safeMath';

export const DPI = 110;
export const PT = DPI / 72;
export const FONT = 'DejaVu Sans, Verdana, Arial, sans-serif';
export const GROUP_COLOURS = ['#1f6fd1', '#e08a00', '#2e9e5b', '#c0392b', '#7b4fc9', '#8c564b', '#d4459a', '#5a6b7a', '#b8a200', '#18a3b8'];
export const TAB10 = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

const lerp = (stops, v) => {
  const s = Math.max(0, Math.min(1, v)); const p = s * (stops.length - 1); const i = Math.min(stops.length - 2, Math.floor(p)); const f = p - i;
  return stops[i].map((c, j) => Math.round(c + (stops[i + 1][j] - c) * f));
};
const VIRIDIS = [[68, 1, 84], [72, 40, 120], [62, 74, 137], [49, 104, 142], [38, 130, 142], [31, 158, 137], [53, 183, 121], [109, 205, 89], [180, 222, 44], [253, 231, 37]];
const MAGMA = [[0, 0, 4], [28, 16, 68], [79, 18, 123], [129, 37, 129], [181, 54, 122], [229, 80, 100], [251, 135, 97], [254, 194, 135], [252, 253, 191]];
export const viridis = (v) => `rgb(${lerp(VIRIDIS, v).join(',')})`;
export const magmaRGB = (v) => lerp(MAGMA, v);
export const grayR = (v) => { const g = Math.round(255 * (1 - Math.max(0, Math.min(1, v)))); return [g, g, g]; };

// matplotlib MaxNLocator(nbins='auto', steps=[1, 2, 2.5, 5, 10]): x axes 3 x fontsize per tick, y axes 2 x; max 9 bins
export function ticks(lo, hi, axisPx, isX, fontPt = 10) {
  const a = Math.min(lo, hi); const b = Math.max(lo, hi);
  if (!(b > a)) return [a];
  const nbins = Math.min(9, Math.max(1, Math.floor((axisPx / PT) / (fontPt * (isX ? 3 : 2)))));
  const raw = (b - a) / nbins; const scale = 10 ** Math.floor(Math.log10(raw));
  for (const st of [1, 2, 2.5, 5, 10].map((v) => v * scale).filter((v) => v >= raw * 0.999)) {
    const out = []; for (let v = Math.floor(a / st + 1e-9) * st; v <= b + st * 1e-9; v += st) if (v >= a - st * 1e-9) out.push(Math.round(v / st) * st);
    if (out.length >= 2) return out;
  }
  return [a, b];
}
export function logTicks(lo, hi) {
  const out = []; for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e += 1) [1, 2, 5].forEach((m) => { const v = m * 10 ** e; if (v >= lo && v <= hi) out.push(v); });
  return out;
}
export function fmtTick(v, step) {
  if (step === undefined) return String(+v.toPrecision(3)).replace('-', '−');
  const d = Math.max(0, -Math.floor(Math.log10(Math.abs(step) + 1e-12)) + (Math.abs(step / 10 ** Math.floor(Math.log10(Math.abs(step)))) === 2.5 ? 1 : 0));
  return (Math.abs(v) < Math.abs(step) * 1e-6 ? 0 : v).toFixed(d).replace('-', '−');
}
export const autolim = (vals, pad = 0.05) => {
  const v = vals.filter((x) => x !== null && x !== undefined && Number.isFinite(x));
  if (!v.length) return [0, 1];
  let lo = amin(v); let hi = amax(v); if (hi === lo) { lo -= 0.5; hi += 0.5; }
  const m = (hi - lo) * pad; return [lo - m, hi + m];
};

/** One axes. r = {x, y, w, h} px; children(X, Y) draws inside (clipped). */
export function Ax({ id, r, xlim, ylim, logX, logY, xlabel, ylabel, title, titleLoc = 'center', xticks, yticks, xticklabels, yticklabels,
  labelX = true, labelY = true, spines = 'all', fontPt = 10, tickFontPt, ylabelColor, right, children, noYTicks, titleSize }) {
  const tfs = (tickFontPt || fontPt) * PT; const fs = fontPt * PT; const tl = 3.5 * PT;
  const tx = (v) => (logX ? Math.log10(v) : v); const ty = (v) => (logY ? Math.log10(v) : v);
  const X = (v) => r.x + ((tx(v) - tx(xlim[0])) / (tx(xlim[1]) - tx(xlim[0]))) * r.w;
  const Y = (v) => r.y + r.h - ((ty(v) - ty(ylim[0])) / (ty(ylim[1]) - ty(ylim[0]))) * r.h;
  const xt = xticks || (logX ? logTicks(xlim[0], xlim[1]) : ticks(xlim[0], xlim[1], r.w, true, tickFontPt || fontPt));
  const yt = noYTicks ? [] : (yticks || (logY ? logTicks(amin(ylim), amax(ylim)) : ticks(ylim[0], ylim[1], r.h, false, tickFontPt || fontPt)));
  const xs = !logX && xt.length > 1 ? Math.abs(xt[1] - xt[0]) : undefined; const ys = !logY && yt.length > 1 ? Math.abs(yt[1] - yt[0]) : undefined;
  const inX = (v) => { const p = X(v); return p >= r.x - 0.5 && p <= r.x + r.w + 0.5; };
  const inY = (v) => { const p = Y(v); return p >= r.y - 0.5 && p <= r.y + r.h + 0.5; };
  const ylabs = yt.filter(inY).map((v, i) => (yticklabels ? yticklabels[i] : fmtTick(v, ys)));
  const ylw = amax(0, ylabs.map((s) => String(s).length)) * tfs * 0.6;
  const side = right ? 1 : -1; const ax0 = right ? r.x + r.w : r.x;
  const sp = spines === 'all' ? `M${r.x},${r.y} H${r.x + r.w} V${r.y + r.h} H${r.x} Z` : (spines === 'lb' ? `M${r.x},${r.y} V${r.y + r.h} H${r.x + r.w}` : (spines === 'b' ? `M${r.x},${r.y + r.h} H${r.x + r.w}` : ''));
  return (
    <g fontFamily={FONT}>
      <clipPath id={id}><rect x={r.x} y={r.y} width={r.w} height={r.h} /></clipPath>
      <g clipPath={`url(#${id})`}>{children && children(X, Y)}</g>
      {!right && sp && <path d={sp} fill="none" stroke="#000" strokeWidth={0.8 * PT} />}
      {!right && xt.filter(inX).map((v, i) => { const x = X(v); return (
        <g key={`x${v}`}><line x1={x} x2={x} y1={r.y + r.h} y2={r.y + r.h + tl} stroke="#000" strokeWidth={0.8 * PT} />
          {labelX && <text x={x} y={r.y + r.h + tl + 3.5 * PT + tfs * 0.8} fontSize={tfs} textAnchor="middle">{xticklabels ? xticklabels[i] : (logX ? fmtTick(v) : fmtTick(v, xs))}</text>}</g>); })}
      {yt.filter(inY).map((v, i) => { const y = Y(v); return (
        <g key={`y${v}`}><line x1={ax0} x2={ax0 + side * tl} y1={y} y2={y} stroke="#000" strokeWidth={0.8 * PT} />
          {labelY && <text x={ax0 + side * (tl + 3.5 * PT)} y={y + tfs * 0.35} fontSize={tfs} textAnchor={right ? 'start' : 'end'} fill={ylabelColor || '#000'}>{ylabs[i]}</text>}</g>); })}
      {xlabel && <text x={r.x + r.w / 2} y={r.y + r.h + tl + 3.5 * PT + tfs * 0.8 + 3.5 * PT + fs} fontSize={fs} textAnchor="middle">{xlabel}</text>}
      {ylabel && <text transform={`translate(${ax0 + side * (tl + 3.5 * PT + ylw + 4 * PT + fs * 0.3)}, ${r.y + r.h / 2}) rotate(${right ? 90 : -90})`}
                       fontSize={fs} textAnchor="middle" fill={ylabelColor || '#000'}>{String(ylabel).split('\n').map((l, j, a) => <tspan key={j} x="0" dy={j ? fs * 1.15 : -(a.length - 1) * fs * 0.55}>{l}</tspan>)}</text>}
      {title && <text x={titleLoc === 'left' ? r.x : r.x + r.w / 2} y={r.y - 6 * PT} fontSize={(titleSize || fontPt * 0.9) * PT} textAnchor={titleLoc === 'left' ? 'start' : 'middle'}>{title}</text>}
    </g>);
}

/** raster image (cells) drawn on a canvas: data[row][col] -> rgb; placed over axes coordinates */
export function rasterHref(rows, cols, rgbAt) {
  const cv = document.createElement('canvas'); cv.width = cols; cv.height = rows;
  const ctx = cv.getContext('2d'); const im = ctx.createImageData(cols, rows);
  for (let r = 0; r < rows; r += 1) for (let c = 0; c < cols; c += 1) { const [a, b, d] = rgbAt(r, c); const p = (r * cols + c) * 4; im.data[p] = a; im.data[p + 1] = b; im.data[p + 2] = d; im.data[p + 3] = 255; }
  ctx.putImageData(im, 0, 0); return cv.toDataURL();
}

export const pts = (arr, X, Y) => arr.filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b)).map(([a, b]) => `${X(a)},${Y(b)}`).join(' ');
export const Marker = ({ x, y, kind = 'o', size = 4, color, fill }) => {
  const s = size * PT * 0.5;
  if (kind === 'o') return <circle cx={x} cy={y} r={s} fill={fill === undefined ? color : fill} stroke={color} strokeWidth={0.8} />;
  if (kind === '.') return <circle cx={x} cy={y} r={s * 0.6} fill={color} />;
  if (kind === 'v') return <path d={`M${x - s},${y - s * 0.8} L${x + s},${y - s * 0.8} L${x},${y + s} Z`} fill={color} />;
  if (kind === '^') return <path d={`M${x - s},${y + s * 0.8} L${x + s},${y + s * 0.8} L${x},${y - s} Z`} fill={color} />;
  if (kind === 'x') return <g stroke={color} strokeWidth={1}><line x1={x - s} x2={x + s} y1={y - s} y2={y + s} /><line x1={x - s} x2={x + s} y1={y + s} y2={y - s} /></g>;
  return null;
};

export function Legend({ x, y, items, fontPt = 7, cols = 1, anchor = 'left', frame = true }) {
  const fs = fontPt * PT; const lh = fs * 1.5; const colW = amax(items.map((it) => it.label.length)) * fs * 0.58 + 34;
  const rows = Math.ceil(items.length / cols); const w = colW * cols + 8; const h = rows * lh + 6;
  const x0 = anchor === 'right' ? x - w : (anchor === 'center' ? x - w / 2 : x);
  return (
    <g fontFamily={FONT}>
      {frame && <rect x={x0} y={y} width={w} height={h} fill="#fff" fillOpacity="0.85" stroke="#ccc" rx="3" />}
      {items.map((it, i) => { const cx = x0 + 6 + Math.floor(i / rows) * colW; const cy = y + 4 + (i % rows) * lh + lh / 2; return (
        <g key={it.label}>
          {it.line && <line x1={cx} x2={cx + 24} y1={cy} y2={cy} stroke={it.color} strokeWidth={(it.lw || 1.2) * PT} strokeDasharray={it.dash} />}
          {it.marker && <Marker x={cx + 12} y={cy} kind={it.marker} color={it.color} size={it.size || 5} />}
          {it.bar && <rect x={cx} y={cy - 3} width={24} height={6} fill={it.color} />}
          <text x={cx + 30} y={cy + fs * 0.35} fontSize={fs}>{it.label}</text>
        </g>); })}
    </g>);
}

export function Figure({ w, h, children, label }) {
  return <svg viewBox={`0 0 ${w} ${h}`} className="mplfig" role="img" aria-label={label} fontFamily={FONT}><rect width={w} height={h} fill="#fff" />{children}</svg>;
}

// the engine's ".-" style: line + small markers
export function LineMarks({ data, X, Y, color, lw = 0.7, ms = 4, kind = '.', dash }) {
  return (<g>
    <polyline points={pts(data, X, Y)} fill="none" stroke={color} strokeWidth={lw * PT} strokeDasharray={dash} />
    {kind && data.filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b)).map(([a, b], i) => <Marker key={i} x={X(a)} y={Y(b)} kind={kind} size={ms} color={color} />)}
  </g>);
}
